'use client';

import { c as _c } from "react/compiler-runtime";
import { PLATFORM_LABELS } from '@shared/all/crosspost';
import { MIN_LEAD_MINUTES } from '@shared/all/domains/scheduledPosts';
import type { CrossPostPlatform } from '@shared/all/validators';
import { useState } from 'react';
import { type RouterOutputs } from '../../utils/trpc';
import { getDisplaySrc } from '../../utils/uploads';
import Button from '../common/Button';
export type ScheduledPostItem = RouterOutputs['scheduledPosts']['list']['posts'][number];
export type RowAction = 'setTime' | 'backToAuto' | 'postNow' | 'remove';

// datetime-local speaks local wall-clock, so toISOString (UTC) would shift the
// value by the viewer's offset. Build the string from the local getters instead.
const toLocalInputValue = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const MS_PER_MINUTE = 60 * 1000;

// The earliest time the server accepts is MIN_LEAD_MINUTES from now
// (scheduledPosts.setTime). The input drops the seconds, so one more minute
// keeps the earliest pickable value from falling just short of it.
const earliestInputValue = () => toLocalInputValue(new Date(Date.now() + (MIN_LEAD_MINUTES + 1) * MS_PER_MINUTE));

// The server's platform enum is wider than the cross-post targets (it also
// names storage connections), so an unknown one falls back to its raw name.
export const platformLabel = (platform: string) => PLATFORM_LABELS[platform as CrossPostPlatform] ?? platform;
export const formatWhen = (date: Date) => date.toLocaleString(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short'
});
type Props = {
  post: ScheduledPostItem;
  busy: boolean;
  onAction: (action: RowAction, scheduledFor?: Date) => void;
};
const ScheduledPostRow = t0 => {
  const $ = _c(55);
  const {
    post,
    busy,
    onAction
  } = t0;
  const [editing, setEditing] = useState(null);
  let failed;
  let reason;
  let t1;
  let t2;
  let t3;
  let t4;
  let when;
  if ($[0] !== post.error || $[1] !== post.manual || $[2] !== post.platforms || $[3] !== post.scheduledFor || $[4] !== post.status) {
    when = new Date(post.scheduledFor);
    failed = post.status === "FAILED";
    const t5 = post.error ?? "unknown reason";
    let t6;
    if ($[12] !== t5) {
      let t7;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = /[.\s]+$/;
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      t6 = t5.replace(t7, "");
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    reason = t6;
    const cost = post.platforms.reduce(_temp, 0);
    t4 = "rounded-xl border border-ds-border-standard bg-background-primary p-4";
    t1 = "flex flex-wrap items-baseline justify-between gap-2";
    const t7 = formatWhen(when);
    const t8 = post.manual ? "Set by you" : "Auto";
    let t9;
    if ($[15] !== t8) {
      t9 = <span className="ml-2 rounded-full border border-ds-border-standard px-2 py-px text-[11px] font-normal text-textSecondary">{t8}</span>;
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    if ($[17] !== t7 || $[18] !== t9) {
      t2 = <p className="text-sm font-emphasis text-textPrimary">{t7}{t9}</p>;
      $[17] = t7;
      $[18] = t9;
      $[19] = t2;
    } else {
      t2 = $[19];
    }
    t3 = post.platforms.length > 0 && <p className="text-xs text-textSecondary">Also to{" "}{post.platforms.map(_temp2).join(", ")}{cost > 0 && ` · ${cost.toLocaleString()} trons paid`}</p>;
    $[0] = post.error;
    $[1] = post.manual;
    $[2] = post.platforms;
    $[3] = post.scheduledFor;
    $[4] = post.status;
    $[5] = failed;
    $[6] = reason;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = when;
  } else {
    failed = $[5];
    reason = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    when = $[11];
  }
  let t5;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t3) {
    t5 = <div className={t1}>{t2}{t3}</div>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== post.title) {
    t6 = post.title && <p className="mt-2 text-sm font-emphasis text-textPrimary">{post.title}</p>;
    $[24] = post.title;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const t7 = post.preview || "No text";
  let t8;
  if ($[26] !== t7) {
    t8 = <p className="mt-1 whitespace-pre-wrap break-words text-sm text-textSecondary">{t7}</p>;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== post.images) {
    t9 = post.images.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{post.images.map(_temp3)}</div>;
    $[28] = post.images;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== post.uploadCount) {
    t10 = post.uploadCount > 0 && <p className="mt-1 text-xs text-textSecondary/70">{post.uploadCount}{" "}{post.uploadCount === 1 ? "attachment" : "attachments"}</p>;
    $[30] = post.uploadCount;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== failed || $[33] !== reason) {
    t11 = failed && <p className="mt-2 rounded-md bg-red-500/10 px-2 py-1 text-xs text-red-500">It did not go out: {reason}. Pick a new time or post it now.</p>;
    $[32] = failed;
    $[33] = reason;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== failed || $[36] !== post.error || $[37] !== reason) {
    t12 = !failed && post.error && <p className="mt-2 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-500">The last try did not go out: {reason}. It will be tried again.</p>;
    $[35] = failed;
    $[36] = post.error;
    $[37] = reason;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== busy || $[40] !== editing || $[41] !== onAction || $[42] !== post.manual || $[43] !== when) {
    t13 = editing !== null ? <div className="mt-3 flex flex-wrap items-center gap-2"><input type="datetime-local" aria-label="When to post" min={earliestInputValue()} value={editing} onChange={e => setEditing(e.target.value)} className="rounded-md border border-ds-border-standard bg-background-light px-2 py-1 text-sm text-textPrimary" /><Button variant="PRIMARY" text="Save time" disabled={busy || !editing} onClick={() => {
        onAction("setTime", new Date(editing));
        setEditing(null);
      }} /><Button variant="TRANSPARENT" text="Cancel" onClick={() => setEditing(null)} /></div> : <div className="mt-3 flex flex-wrap items-center gap-2"><Button variant="SECONDARY" text="Change time" disabled={busy} onClick={() => setEditing(toLocalInputValue(when))} />{post.manual && <Button variant="SECONDARY" text="Back to auto" disabled={busy} onClick={() => onAction("backToAuto")} />}<Button variant="SECONDARY" text="Post now" disabled={busy} onClick={() => onAction("postNow")} /><Button variant="TRANSPARENT" text="Move to drafts" disabled={busy} tooltip="Takes it off the schedule and refunds its cross-posts" onClick={() => onAction("remove")} /></div>;
    $[39] = busy;
    $[40] = editing;
    $[41] = onAction;
    $[42] = post.manual;
    $[43] = when;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== t4 || $[50] !== t5 || $[51] !== t6 || $[52] !== t8 || $[53] !== t9) {
    t14 = <li className={t4}>{t5}{t6}{t8}{t9}{t10}{t11}{t12}{t13}</li>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = t4;
    $[50] = t5;
    $[51] = t6;
    $[52] = t8;
    $[53] = t9;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  return t14;
};
export default ScheduledPostRow;
function _temp(sum, p) {
  return sum + p.tronsCost;
}
function _temp2(p_0) {
  return platformLabel(p_0.platform);
}
function _temp3(image) {
  return <img key={image.id} src={getDisplaySrc(image)} alt="" loading="lazy" className="size-20 rounded-md border border-ds-border-subtle object-cover" />;
}